import { useMemo, useState } from "react";
import { ActiveFilters, COUNTRY_CODES, FacetFilter, LANGUAGE_CODES, geoName, type Facet, type FacetFilterValue } from "@fox1ck-org/ui";
import { LanderType, LanderUsageFilter, RiskStatus } from "@gen/tracker/v1/lander_pb";

import { getLocale, useT } from "@/lib/i18n";
import type { TableParams } from "@/lib/hooks/use-table-params";

import { LANDER_TYPES, RISKS, RISK_SLUG, TYPE_SLUG, riskFromSlug, riskLabelKey, typeFromSlug, typeLabelKey } from "./kinds";

/**
 * The library's filters (boards LanderList, LanderTiles): Тип · Язык · Страна
 * · Риск · Используется — the package's FacetFilter, the chips always on the
 * row («Тип Все»), every value in the address (`?type=vsl&lang=pl&used=yes`)
 * and every one answered by the server (ListLanders), never by the page.
 */
export const FACET_IDS = ["type", "lang", "country", "risk", "used"] as const;
export type FacetId = (typeof FACET_IDS)[number];

export interface LanderListFilter {
  types: LanderType[];
  languages: string[];
  countries: string[];
  risks: RiskStatus[];
  usage: LanderUsageFilter;
}

const split = (raw: string) => raw.split(",").map((v) => v.trim()).filter(Boolean);

/** The address's filters in the RPC's words. Pure. */
export function readFilter(get: (key: string) => string): LanderListFilter {
  const used = get("used");
  return {
    types: split(get("type")).flatMap((s) => typeFromSlug(s) ?? []),
    languages: split(get("lang")).map((l) => l.toLowerCase()),
    countries: split(get("country")).map((c) => c.toUpperCase()),
    risks: split(get("risk")).flatMap((s) => riskFromSlug(s) ?? []),
    usage: used === "yes" ? LanderUsageFilter.IN_USE : used === "no" ? LanderUsageFilter.UNUSED : LanderUsageFilter.UNSPECIFIED,
  };
}

/** The address's filters as the FacetFilter's value. Pure. */
export function facetValue(get: (key: string) => string): FacetFilterValue {
  const out: Record<string, string[]> = {};
  for (const id of FACET_IDS) {
    const v = split(get(id));
    if (v.length > 0) out[id] = v;
  }
  return out;
}

export function useLanderFacets(): Facet[] {
  const t = useT();
  return useMemo(() => {
    const locale = getLocale();
    return [
      {
        id: "type",
        label: t("landers.facet.type"),
        kind: "options",
        options: LANDER_TYPES.map((x) => ({ value: TYPE_SLUG[x], label: t(typeLabelKey(x)) })),
      },
      {
        id: "lang",
        label: t("landers.facet.lang"),
        kind: "options",
        searchable: true,
        options: LANGUAGE_CODES.map((c) => ({ value: c, label: `${c} · ${geoName(c, "language", locale)}` })),
      },
      {
        id: "country",
        label: t("landers.facet.country"),
        kind: "options",
        searchable: true,
        options: COUNTRY_CODES.map((c) => ({ value: c, label: `${c} · ${geoName(c, "country", locale)}` })),
      },
      {
        id: "risk",
        label: t("landers.facet.risk"),
        kind: "options",
        options: RISKS.map((r) => ({ value: RISK_SLUG[r], label: t(riskLabelKey(r)) })),
      },
      {
        id: "used",
        label: t("landers.facet.used"),
        kind: "options",
        options: [
          { value: "yes", label: t("landers.facet.used.yes") },
          { value: "no", label: t("landers.facet.used.no") },
        ],
      },
    ] satisfies Facet[];
  }, [t]);
}

export function LanderFacets({ params }: { params: TableParams }) {
  const t = useT();
  const facets = useLanderFacets();
  const [active, setActive] = useState<string | null>(null);
  const value = facetValue(params.getFilter);
  const apply = (next: FacetFilterValue) => {
    const patch: Record<string, string | null> = {};
    for (const id of FACET_IDS) {
      const v = next[id];
      // «Используется» is one answer, not a set: the last one ticked wins.
      const list = Array.isArray(v) ? (id === "used" ? v.slice(-1) : v) : [];
      patch[id] = list.length > 0 ? list.join(",") : null;
    }
    params.setFilters(patch);
  };
  return (
    <>
      <ActiveFilters
        value={value}
        facets={facets}
        alwaysShow={FACET_IDS}
        maxValues={3}
        editingId={active}
        onRemove={(id) => apply({ ...value, [id]: [] })}
        onEdit={(id) => setActive(id)}
        labels={{
          list: t("landers.filter.active"),
          remove: (f) => t("landers.filter.remove", { name: f }),
          edit: (f) => t("landers.filter.edit", { name: f }),
          any: () => t("landers.filter.any"),
        }}
      />
      <FacetFilter
        facets={facets}
        value={value}
        onChange={apply}
        activeFacetId={active}
        onActiveFacetChange={setActive}
        labels={{
          trigger: t("landers.filter.add"),
          facets: t("landers.filter.facets"),
          search: (f) => t("landers.filter.search", { name: f }),
          apply: t("landers.filter.apply"),
          reset: t("landers.filter.reset"),
          noOptions: t("landers.filter.nothing"),
          loading: t("landers.filter.loading"),
          back: t("landers.filter.back"),
        }}
      />
    </>
  );
}
